import { alpha } from '@mui/material/styles';

import { MOBILE_BOTTOM_NAV } from 'constants/ui';

import {
  GLASS_BG_ALPHA,
  NAV_ACCENT_COLOR,
  NAV_ACTIVE_GLOW_ALPHA,
  NAV_ACTIVE_GRADIENT,
  NAV_ACTIVE_ICON_COLOR,
  NAV_ACTIVE_SHEEN_ALPHA,
  NAV_BAR_SHADOW_ALPHA,
  NAV_BORDER_ALPHA,
  NAV_GLASS_BLUR,
  NAV_GLASS_RIM_ALPHA,
  NAV_GLASS_TINT_ALPHA,
} from './BottomNav.constants';
import { NAV_HELD_GLOW_ALPHA, NAV_HELD_SHADOW_ALPHA } from './BottomNav.groups.constants';

export const barStyles = {
  position: 'fixed',
  left: '12px',
  right: '12px',
  bottom: `calc(${MOBILE_BOTTOM_NAV.GAP}px + env(safe-area-inset-bottom, 0px))`,
  zIndex: ({ zIndex }) => zIndex.appBar,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  gap: '2px',
  padding: '8px',
  borderRadius: '24px',
  backgroundColor: ({ palette }) => alpha(palette.background.paper, GLASS_BG_ALPHA),
  backgroundImage: `linear-gradient(135deg, ${alpha(
    NAV_ACCENT_COLOR,
    NAV_GLASS_TINT_ALPHA,
  )} 0%, transparent 60%)`,
  backdropFilter: NAV_GLASS_BLUR,
  WebkitBackdropFilter: NAV_GLASS_BLUR,
  border: '1px solid',
  borderColor: ({ palette }) => alpha(palette.divider, NAV_BORDER_ALPHA),
  boxShadow: ({ palette }) =>
    [
      `0px 8px 28px ${alpha(palette.common.black, NAV_BAR_SHADOW_ALPHA)}`,
      `inset 0px 1px 0px ${alpha(palette.common.white, NAV_GLASS_RIM_ALPHA)}`,
    ].join(', '),
};

/** Slides the bar off-screen (down) when hidden by the quick-return scroll behavior. */
export const getBarVisibilityStyles = ({ isHidden }) => ({
  transform: isHidden ? 'translateY(calc(100% + 16px))' : 'translateY(0)',
  transition: 'transform 240ms ease',
  willChange: 'transform',
});

export const getTabButtonStyles = ({ isActive }) => ({
  flex: 1,
  minWidth: '44px',
  minHeight: '52px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '4px 2px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: '10px',
  color: isActive ? NAV_ACCENT_COLOR : 'text.secondary',
  textDecoration: 'none',
  position: 'relative',
  transition: 'transform 160ms ease',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.96)',
  },
});

export const getTabIconWrapperStyles = ({ isActive }) => ({
  width: '44px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  color: isActive ? NAV_ACTIVE_ICON_COLOR : 'text.secondary',
  background: isActive ? NAV_ACTIVE_GRADIENT : 'transparent',
  boxShadow: isActive
    ? ({ palette }) =>
        [
          `0px 4px 14px ${alpha(NAV_ACCENT_COLOR, NAV_ACTIVE_GLOW_ALPHA)}`,
          `inset 0px 1px 1px ${alpha(palette.common.white, NAV_ACTIVE_SHEEN_ALPHA)}`,
        ].join(', ')
    : 'none',
  transition: 'all 200ms ease',
});

export const tabLabelStyles = {
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * Icon wrapper styles for the held/long-pressed state:
 * lifts + gains an indigo glow ring so the user knows the card is opening.
 */
export const heldTabIconWrapperStyles = {
  width: '44px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  color: NAV_ACTIVE_ICON_COLOR,
  background: NAV_ACTIVE_GRADIENT,
  boxShadow: [
    `0 0 0 4px ${alpha(NAV_ACCENT_COLOR, NAV_HELD_GLOW_ALPHA)}`,
    `0 8px 22px ${alpha(NAV_ACCENT_COLOR, NAV_HELD_SHADOW_ALPHA)}`,
  ].join(', '),
  transition: 'all 200ms ease',
  transform: 'translateY(-3px) scale(1.04)',
};

/**
 * Two-dot cue rendered at the top-right of a group tab's icon wrapper.
 * Implemented as a pseudo-element overlay — this styles wrapper carries
 * the ::after declaration.
 */
export const groupCueDotStyles = {
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '2px',
    right: '7px',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: 'text.disabled',
    boxShadow: '6px 0 0 -0.5px',
    color: 'text.disabled',
    opacity: 0.65,
    transition: 'all 200ms',
    pointerEvents: 'none',
  },
};

/**
 * Two-dot cue styles when the tab is active or held (white dots on gradient pill).
 */
export const groupCueDotActiveStyles = {
  '&::after': {
    backgroundColor: 'rgba(255,255,255,0.95)',
    color: 'rgba(255,255,255,0.95)',
    opacity: 0.95,
  },
};

/** Relative wrapper for the icon + badge overlay. */
export const tabBadgeAnchorStyles = {
  position: 'relative',
  display: 'inline-flex',
};

/** Absolute-positioned unread-count pill in the top-right corner of the icon. */
export const tabBadgeStyles = {
  position: 'absolute',
  top: '-6px',
  right: '-8px',
  pointerEvents: 'none',
};
